import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPopover,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import {
  addOutline,
  bookmarkOutline,
  closeCircleOutline,
  saveOutline,
  settingsOutline,
  swapHorizontalOutline,
  trashOutline,
} from "ionicons/icons";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COLUMN_TYPE_LABELS, TrackingColumnDef, UserColumnType } from "./trackingColumns";
import { ColumnLayout, trackingApi, TrackingRowData, TrackingView, ViewSnapshot } from "./trackingApi";
import AIModal from "../../elements/AIModal";
import ConfirmActionModal from "./ConfirmActionModal";

// Customer and Building cells link to their pages, the same way the other tables do (/?id=<uuid>&sec=...).
// Plain links, so ctrl/cmd-click and middle-click open them in a new tab.
const linkStyle: React.CSSProperties = { color: "var(--ion-color-primary)", textDecoration: "underline" };

const CustomerLink: React.FC<any> = ({ value, data }) =>
  data?.customer_id ? (
    <a href={`/?id=${data.customer_id}&sec=customers`} style={linkStyle}>
      {value}
    </a>
  ) : (
    <>{value}</>
  );

const BuildingLink: React.FC<any> = ({ value, data }) =>
  data?.building_id ? (
    <a href={`/?id=${data.building_id}&sec=buildings`} style={linkStyle}>
      {value}
    </a>
  ) : (
    <>{value}</>
  );

// True/false shown as a filled cell, green for true and red for false, instead of a checkbox.
// A value that isn't set (null) stays unfilled.
const StatusFill: React.FC<any> = ({ value, colDef }) => {
  const label = `${colDef?.headerName ?? "Status"}: ${value === true ? "True" : value === false ? "False" : "Not set"}`;
  return <span title={label} aria-label={label} style={{ display: "block", width: "100%", height: "100%" }} />;
};

// Column header: name (click to sort) plus one "⋮" menu, also opened by right-clicking the header.
// Hiding a column is a personal view setting that works for every column, fixed ones included.
// Deleting removes the column and its data for everyone, so it is limited to editors and to non-fixed columns.
const HeaderWithMenu: React.FC<any> = (props) => {
  const { column, onRemove, onHide, canEdit, displayName, progressSort } = props;
  const [menuEvent, setMenuEvent] = useState<Event | undefined>();

  const openMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuEvent(e.nativeEvent);
  };

  return (
    <div onContextMenu={openMenu} style={{ display: "flex", alignItems: "center", width: "100%", gap: 6 }}>
      <span style={{ flex: 1, cursor: "pointer", fontWeight: 600 }} onClick={(e) => progressSort(e.shiftKey)}>
        {displayName}
      </span>
      <button
        title="Column options"
        aria-label={`${column.label} column options`}
        onClick={openMenu}
        style={{ border: "none", background: "transparent", cursor: "pointer", opacity: 0.6, fontSize: 16, padding: "0 2px" }}
      >
        ⋮
      </button>
      <IonPopover
        isOpen={!!menuEvent}
        event={menuEvent}
        onDidDismiss={() => setMenuEvent(undefined)}
        dismissOnSelect
        showBackdrop={false}
      >
        <IonList lines="none">
          <IonItem button detail={false} onClick={() => onHide(column.id)}>
            <IonLabel>Hide column</IonLabel>
          </IonItem>
          {column.required ? (
            <IonItem disabled detail={false}>
              <IonLabel className="ion-text-wrap" style={{ fontSize: 12 }}>
                Fixed column: it can't be deleted
              </IonLabel>
            </IonItem>
          ) : canEdit ? (
            <IonItem
              button
              detail={false}
              onClick={() => onRemove(column)}
            >
              <IonLabel color="danger">Delete column…</IonLabel>
            </IonItem>
          ) : null}
        </IonList>
      </IonPopover>
    </div>
  );
};

// The sheet has one row per building, created by the server, so rows are only edited here, never added or removed.
const TrackingGrid: React.FC = () => {
  const [columns, setColumns] = useState<TrackingColumnDef[]>([]);
  const [rows, setRows] = useState<TrackingRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [views, setViews] = useState<TrackingView[]>([]);
  // One shared "are you sure?" dialog. `confirmOpen` is separate so the text stays put while it animates closed.
  const [confirm, setConfirm] = useState<{
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    confirmColor: string;
    action: () => void;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState<string[]>([]); // columns this user has hidden
  const [newViewName, setNewViewName] = useState("");
  const [viewError, setViewError] = useState(""); // errors from the Saved Views section of the modal
  const [columnFiltersOn, setColumnFiltersOn] = useState(false);
  const [shownCount, setShownCount] = useState<number | null>(null); // rows left after search/filters
  const layoutRef = useRef<ColumnLayout | null>(null); // saved layout, applied when the grid is ready
  const gridApi = useRef<GridApi | null>(null);
  const saveTimer = useRef<number | undefined>();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modalError, setModalError] = useState(""); // validation errors for the Add Column form
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<UserColumnType>("text");
  const [newOptions, setNewOptions] = useState("");
  const [error, setError] = useState("");

  const loadSheet = useCallback(async () => {
    try {
      const sheet = await trackingApi.getSheet();
      setColumns(sheet.columns);
      setRows(sheet.rows);
      setCanEdit(sheet.can_edit);
      layoutRef.current = sheet.layout;
      setViews(sheet.views);
      setError("");
    } catch (e: any) {
      setError(e.message || "Could not load the tracking sheet.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  // Run a server mutation; on failure show the message and resync with the server.
  const mutate = useCallback(
    async (fn: () => Promise<void>) => {
      try {
        setError("");
        await fn();
      } catch (e: any) {
        setError(e.message || "Something went wrong.");
        await loadSheet();
      }
    },
    [loadSheet]
  );

  const removeColumn = useCallback(
    (id: string) =>
      mutate(async () => {
        await trackingApi.deleteColumn(id);
        setColumns((cols) => cols.filter((c) => c.id !== id));
        setRows((rs) =>
          rs.map((r) => {
            const { [id]: _drop, ...keep } = r;
            return keep as TrackingRowData;
          })
        );
      }),
    [mutate]
  );

  const askConfirm = useCallback((c: NonNullable<typeof confirm>) => {
    setConfirm(c);
    setConfirmOpen(true);
  }, []);

  const requestRemoveColumn = useCallback(
    (col: TrackingColumnDef) =>
      askConfirm({
        title: "Delete Column",
        message: `Delete the "${col.label}" column and its data for everyone? This cannot be undone.`,
        confirmLabel: "Delete",
        confirmColor: "danger",
        action: () => removeColumn(col.id),
      }),
    [askConfirm, removeColumn]
  );

  // Save the user's working layout a moment after they stop changing it
  const scheduleLayoutSave = useCallback(() => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      const state = gridApi.current?.getColumnState();
      if (!state) return;
      // null (not undefined) for unset props so that applying the state later clears them
      const layout: ColumnLayout = state.map((c) => ({
        colId: c.colId,
        width: c.width,
        hide: !!c.hide,
        pinned: c.pinned ?? null,
        flex: c.flex ?? null,
        sort: c.sort ?? null,
        sortIndex: c.sortIndex ?? null,
      }));
      layoutRef.current = layout;
      trackingApi.saveLayout(layout).catch((err) => setError(err.message || "Could not save your layout."));
    }, 1000);
  }, []);

  // Hiding is a personal view setting: it never deletes the column or its data
  const setColumnShown = useCallback(
    (id: string, shown: boolean) => {
      gridApi.current?.setColumnsVisible([id], shown);
      scheduleLayoutSave();
    },
    [scheduleLayoutSave]
  );
  const hideColumn = useCallback((id: string) => setColumnShown(id, false), [setColumnShown]);

  const colDefs = useMemo<ColDef[]>(() => {
    const defs: ColDef[] = columns.map((c) => {
      const base: ColDef = {
        field: c.id,
        colId: c.id,
        headerName: c.label,
        editable: canEdit && !c.readonly,
        cellStyle: c.readonly ? { color: "var(--ion-color-medium)" } : undefined,
        headerComponent: HeaderWithMenu,
        headerComponentParams: { column: c, onRemove: requestRemoveColumn, onHide: hideColumn, canEdit },
        minWidth: 150,
        flex: 1,
      };
      switch (c.type) {
        // customer and building come from the building the row belongs to, so they are read-only
        case "customer":
        case "building":
          return {
            ...base,
            editable: false,
            pinned: c.type === "customer" ? "left" : undefined,
            cellDataType: "text",
            cellRenderer: c.type === "customer" ? CustomerLink : BuildingLink,
          };
        case "datetime":
          // ISO date and time from the server, shown in the user's local time. The date filter compares days.
          return {
            ...base,
            cellDataType: "date",
            valueGetter: (p) => (p.data?.[c.id] ? new Date(p.data[c.id]) : null),
            valueFormatter: (p) =>
              p.value instanceof Date && !isNaN(p.value.getTime())
                ? p.value.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
                : "",
            minWidth: 170,
          };
        case "status":
          return {
            ...base,
            cellDataType: "boolean",
            cellRenderer: StatusFill,
            cellStyle: (p) =>
              p.value === true
                ? { backgroundColor: "var(--ion-color-success)" }
                : p.value === false
                ? { backgroundColor: "var(--ion-color-danger)" }
                : null,
            minWidth: 130,
          };
        case "number":
          return { ...base, cellDataType: "number" };
        case "date":
          return { ...base, cellDataType: "dateString" };
        case "checkbox":
          // fixed checkbox columns are saved to the building; keep them narrow
          return {
            ...base,
            cellDataType: "boolean",
            flex: c.required ? undefined : base.flex,
            width: c.required ? 150 : undefined,
            minWidth: c.required ? 110 : base.minWidth,
          };
        case "select":
          return {
            ...base,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["", ...(c.options ?? [])] },
          };
        default:
          return { ...base, cellDataType: "text" };
      }
    });
    // leading row-number column like the reference sheet
    const rowNum: ColDef = {
      headerName: "",
      colId: "_rownum",
      valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
      width: 60,
      pinned: "left",
      editable: false,
      sortable: false,
      filter: false,
      floatingFilter: false,
      resizable: false,
      suppressMovable: true,
    };
    return [rowNum, ...defs];
  }, [columns, requestRemoveColumn, hideColumn, canEdit]);

  // filter: true picks the right filter per column type (text, number, date, true/false).
  // The floating filter row is how users reach them, since our custom header has no filter menu.
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      filterParams: { buttons: ["reset"], debounceMs: 200 },
    }),
    []
  );

  const updateShownCount = () => setShownCount(gridApi.current?.getDisplayedRowCount() ?? null);

  const onFilterChanged = () => {
    updateShownCount();
    setColumnFiltersOn(Object.keys(gridApi.current?.getFilterModel() ?? {}).length > 0);
  };

  const clearFilters = () => {
    setSearch("");
    gridApi.current?.setFilterModel(null);
  };

  const applyLayout = () => {
    if (layoutRef.current?.length) gridApi.current?.applyColumnState({ state: layoutRef.current, applyOrder: true });
  };

  const onGridReady = (e: GridReadyEvent) => {
    gridApi.current = e.api;
    applyLayout();
    syncHiddenCols();
  };

  const syncHiddenCols = () =>
    setHiddenCols((gridApi.current?.getColumnState() ?? []).filter((c) => c.hide).map((c) => c.colId));

  // Null (not undefined) for unset props so that applying the state later clears them (sort, pinning, flex).
  const captureColumnState = (): ColumnLayout =>
    (gridApi.current?.getColumnState() ?? []).map((c) => ({
      colId: c.colId,
      width: c.width,
      hide: !!c.hide,
      pinned: c.pinned ?? null,
      flex: c.flex ?? null,
      sort: c.sort ?? null,
      sortIndex: c.sortIndex ?? null,
    }));

  const captureView = (): ViewSnapshot => ({
    column_state: captureColumnState(),
    filter_model: gridApi.current?.getFilterModel() ?? {},
    search: search.trim(),
  });

  const applyView = (v: TrackingView) => {
    const api = gridApi.current;
    if (!api) return;
    api.applyColumnState({ state: v.column_state, applyOrder: true });
    // drop filters on columns that have since been deleted
    const known = new Set(columns.map((c) => c.id));
    api.setFilterModel(Object.fromEntries(Object.entries(v.filter_model).filter(([id]) => known.has(id))));
    setSearch(v.search);
    // the layout autosave only reacts to user drags, so persist this arrangement as the working layout
    layoutRef.current = captureColumnState();
    trackingApi.saveLayout(layoutRef.current).catch(() => undefined);
  };

  // View actions report their errors inside the modal and leave the sheet alone (no resync on failure).
  const runViewAction = async (fn: () => Promise<void>) => {
    try {
      setViewError("");
      await fn();
    } catch (e: any) {
      setViewError(e.message || "Something went wrong.");
    }
  };

  const saveNewView = () => {
    const name = newViewName.trim();
    if (!name) return setViewError("Give the view a name.");
    runViewAction(async () => {
      const created = await trackingApi.createView(name, captureView());
      setViews((vs) => [...vs, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewViewName("");
    });
  };

  const overwriteView = (v: TrackingView) => {
    askConfirm({
      title: "Update View",
      message: `Replace "${v.name}" with the current layout, filters and search?`,
      confirmLabel: "Update",
      confirmColor: "primary",
      action: () =>
        runViewAction(async () => {
          const updated = await trackingApi.updateView(v.id, captureView());
          setViews((vs) => vs.map((x) => (x.id === v.id ? updated : x)));
        }),
    });
  };

  const deleteView = (v: TrackingView) => {
    askConfirm({
      title: "Delete View",
      message: `Delete the view "${v.name}"? This cannot be undone.`,
      confirmLabel: "Delete",
      confirmColor: "danger",
      action: () =>
        runViewAction(async () => {
          await trackingApi.deleteView(v.id);
          setViews((vs) => vs.filter((x) => x.id !== v.id));
        }),
    });
  };

  // Save the user's own layout (order, widths, pinning, sort) a moment after they stop changing it.
  // Only user-driven changes count, so applying a saved layout doesn't trigger a save.
  const onLayoutChanged = (e: any) => {
    if (e.finished === false || !String(e.source ?? "").startsWith("ui")) return;
    scheduleLayoutSave();
  };

  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  const resetLayout = () =>
    mutate(async () => {
      await trackingApi.resetLayout();
      layoutRef.current = null;
      gridApi.current?.resetColumnState();
    });

  const onCellValueChanged = (e: CellValueChangedEvent) => {
    const field = e.colDef.field;
    if (!field || e.oldValue === e.newValue) return;
    const id: string = e.data._rowId;
    const value = e.newValue === undefined ? null : e.newValue;
    setRows((rs) => rs.map((r) => (r._rowId === id ? { ...r, [field]: value } : r)));
    mutate(async () => {
      await trackingApi.updateRow(id, { data: { [field]: value } });
    });
  };

  const closeSettings = () => {
    setSettingsOpen(false);
    setModalError("");
    setViewError("");
  };

  const resetColumns = () => {
    askConfirm({
      title: "Reset Columns",
      message: "Restore the default columns? Custom columns and their data will be removed for everyone.",
      confirmLabel: "Reset Columns",
      confirmColor: "danger",
      action: () => {
        closeSettings();
        mutate(async () => {
          await trackingApi.resetColumns();
          await loadSheet();
        });
      },
    });
  };

  const confirmResetLayout = () => {
    closeSettings();
    resetLayout();
  };

  const addColumn = async () => {
    const label = newLabel.trim();
    if (!label) return setModalError("Column name is required.");
    const options = newOptions.split(",").map((o) => o.trim()).filter(Boolean);
    if (newType === "select" && !options.length) return setModalError("Enter at least one dropdown option.");
    try {
      const col = await trackingApi.addColumn({
        label,
        type: newType,
        options: newType === "select" ? options : undefined,
      });
      setColumns((cols) => [...cols, col]);
      setNewLabel("");
      setNewOptions("");
      setNewType("text");
      closeSettings();
    } catch (e: any) {
      setModalError(e.message || "Could not add the column.");
    }
  };

  const sectionTitle: React.CSSProperties = { margin: "16px 0 4px", fontSize: 15, fontWeight: 600 };
  const hint: React.CSSProperties = { margin: "0 0 8px", fontSize: 13, color: "var(--ion-color-medium)" };

  return (
    <div style={{ padding: "0 1rem 1rem" }}>
      <IonLoading isOpen={loading} message="Loading..." />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <IonSearchbar
          value={search}
          debounce={200}
          placeholder="Search all columns"
          onIonInput={(e) => setSearch(String(e.detail.value ?? ""))}
          style={{ padding: 0, minHeight: 36, maxWidth: 500 }}
        />
        {(columnFiltersOn || search.trim() !== "") && (
          <IonButton size="small" fill="clear" onClick={clearFilters}>
            <IonIcon slot="start" icon={closeCircleOutline} /> Clear Filters
          </IonButton>
        )}
        {shownCount !== null && shownCount !== rows.length && (
          <span style={{ fontSize: 13, color: "var(--ion-color-medium)" }}>
            Showing {shownCount} of {rows.length}
          </span>
        )}
        {hiddenCols.length > 0 && (
          <IonButton
            size="small"
            fill="clear"
            title="Open Table Settings to show them again"
            onClick={() => setSettingsOpen(true)}
          >
            {hiddenCols.length} hidden column{hiddenCols.length === 1 ? "" : "s"}
          </IonButton>
        )}
        {!loading && !canEdit && (
          <span style={{ fontSize: 13, color: "var(--ion-color-medium)" }}>View only: you can't edit this sheet.</span>
        )}
        {error && <span style={{ color: "var(--ion-color-danger)", fontSize: 13 }}>{error}</span>}
        <IonButton size="small" fill="clear" id="tracking-views-btn" style={{ marginLeft: "auto" }}>
          <IonIcon slot="start" icon={bookmarkOutline} /> Views{views.length ? ` (${views.length})` : ""}
        </IonButton>
        <IonPopover trigger="tracking-views-btn" dismissOnSelect>
          <IonList>
            {views.length === 0 && (
              <IonItem lines="none">
                <IonLabel className="ion-text-wrap" style={{ fontSize: 13 }}>
                  No saved views yet. Save one from Table Settings (the gear icon).
                </IonLabel>
              </IonItem>
            )}
            {views.map((v) => (
              <IonItem key={v.id} button detail={false} lines="none" onClick={() => applyView(v)}>
                <IonLabel>{v.name}</IonLabel>
              </IonItem>
            ))}
          </IonList>
        </IonPopover>
        <IonButton
          size="small"
          fill="clear"
          title="Table settings"
          aria-label="Table settings"
          onClick={() => setSettingsOpen(true)}
        >
          <IonIcon slot="icon-only" icon={settingsOutline} />
        </IonButton>
      </div>

      <AIModal opened={settingsOpen} setOpened={closeSettings} title="Table Settings" style={{ "--max-height": "90%" }}>
        <h3 className="text-dark-blue" style={sectionTitle}>Saved Views</h3>
        <p style={hint}>
          Save the current column order, sort, filters and search as a named view, then switch to it from the Views
          button. Views are private to you.
        </p>
        <IonItem lines="full">
          <IonLabel position="stacked">View name</IonLabel>
          <IonInput
            value={newViewName}
            maxlength={60}
            onIonInput={(e) => setNewViewName(String(e.detail.value ?? ""))}
          />
        </IonItem>
        {viewError && <p style={{ color: "var(--ion-color-danger)", fontSize: 13 }}>{viewError}</p>}
        <IonButton size="small" style={{ marginTop: 8 }} onClick={saveNewView}>
          <IonIcon slot="start" icon={saveOutline} /> Save Current View
        </IonButton>
        {canEdit && (
          <>
            <h3 className="text-dark-blue" style={{ ...sectionTitle, marginTop: 0 }}>Add Column</h3>
            <p style={hint}>New columns are shared with everyone who uses this sheet.</p>
            <IonItem lines="full">
              <IonLabel position="stacked">Column name</IonLabel>
              <IonInput value={newLabel} onIonInput={(e) => setNewLabel(String(e.detail.value ?? ""))} />
            </IonItem>
            <IonItem lines="full">
              <IonLabel position="stacked">Type</IonLabel>
              <IonSelect value={newType} onIonChange={(e) => setNewType(e.detail.value)}>
                {(Object.keys(COLUMN_TYPE_LABELS) as UserColumnType[]).map((t) => (
                  <IonSelectOption key={t} value={t}>
                    {COLUMN_TYPE_LABELS[t]}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            {newType === "select" && (
              <IonItem lines="full">
                <IonLabel position="stacked">Options (comma separated)</IonLabel>
                <IonInput value={newOptions} onIonInput={(e) => setNewOptions(String(e.detail.value ?? ""))} />
              </IonItem>
            )}
            {modalError && <p style={{ color: "var(--ion-color-danger)", fontSize: 13 }}>{modalError}</p>}
            <IonButton size="small" style={{ marginTop: 8 }} onClick={addColumn}>
              <IonIcon slot="start" icon={addOutline} /> Create Column
            </IonButton>

            {/* <h3 className="text-dark-blue" style={sectionTitle}>Reset Columns</h3>
            <p style={hint}>Restore the default columns. Custom columns and their data are removed for everyone.</p>
            <IonButton size="small" color="danger" fill="outline" onClick={resetColumns}>
              <IonIcon slot="start" icon={refreshOutline} /> Reset Columns
            </IonButton> */}
          </>
        )}

        <h3 className="text-dark-blue" style={canEdit ? sectionTitle : { ...sectionTitle, marginTop: 0 }}>Visible Columns</h3>
        <p style={hint}>
          Choose which columns you see, including the fixed ones. This only changes your own view; hidden columns keep
          their data.
        </p>
        <IonList lines="none">
          {columns.map((c) => (
            <IonItem key={c.id}>
              <IonCheckbox
                labelPlacement="end"
                checked={!hiddenCols.includes(c.id)}
                onIonChange={(e) => setColumnShown(c.id, e.detail.checked)}
              >
                {c.label}
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>
        {hiddenCols.length > 0 && (
          <IonButton
            size="small"
            fill="clear"
            onClick={() => {
              gridApi.current?.setColumnsVisible(hiddenCols, true);
              scheduleLayoutSave();
            }}
          >
            Show all columns
          </IonButton>
        )}

                
        {views.length > 0 && (
          <IonList lines="full">
            {views.map((v) => (
              <IonItem key={v.id}>
                <IonLabel>{v.name}</IonLabel>
                <IonButton slot="end" size="small" fill="clear" onClick={() => overwriteView(v)}>
                  Update
                </IonButton>
                <IonButton
                  slot="end"
                  size="small"
                  fill="clear"
                  color="danger"
                  aria-label={`Delete view ${v.name}`}
                  onClick={() => deleteView(v)}
                >
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              </IonItem>
            ))}
          </IonList>
        )}

        <h3 className="text-dark-blue" style={sectionTitle}>Reset Layout</h3>
        <p style={hint}>Restore the default column order, widths and sorting. This only affects your own view.</p>
        <IonButton size="small" fill="outline" onClick={confirmResetLayout}>
          <IonIcon slot="start" icon={swapHorizontalOutline} /> Reset Layout
        </IonButton>
      </AIModal>

      <ConfirmActionModal
        opened={confirmOpen}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          confirm?.action();
        }}
      />

      <div className="ag-theme-quartz" style={{ height: "calc(100vh - 260px)", minHeight: 320, width: "100%" }}>
        {!loading && (
        <AgGridReact
          rowData={rows}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          getRowId={(p) => p.data._rowId}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          stopEditingWhenCellsLoseFocus
          maintainColumnOrder
          quickFilterText={search}
          onFilterChanged={onFilterChanged}
          onModelUpdated={updateShownCount}
          onColumnMoved={onLayoutChanged}
          onColumnResized={onLayoutChanged}
          onColumnPinned={onLayoutChanged}
          onSortChanged={onLayoutChanged}
          onColumnVisible={syncHiddenCols}
        />
        )}
      </div>
    </div>
  );
};

export default TrackingGrid;
